// src/utils/imageProcessing.ts
// V206.11: Automatic high-res Google image processing with 5:4 ratio

import { SupabaseClient } from '@supabase/supabase-js';

export interface ProcessedImage {
  blob: Blob;
  url: string;
  width: number;
  height: number;
}

/**
 * Get high-resolution Google image URL (2048x2048)
 * Converts s96 or any size parameter to s2048 for maximum quality
 */
export function getHighResGoogleImageUrl(originalUrl: string): string {
  if (!originalUrl) return originalUrl;
  
  if (originalUrl.includes('googleusercontent.com')) {
    // Remove any existing size parameter and add s2048
    const baseUrl = originalUrl.split('=s')[0];
    return `${baseUrl}=s2048`; // 2048x2048 high-res
  }
  
  return originalUrl;
}

/**
 * Crop image to 4:5 aspect ratio (portrait, 1080x1350)
 * Uses center crop to maintain subject focus
 * V167: Changed to 4:5 portrait ratio, WebP format, <100KB file size
 */
export async function cropImageTo4x5(
  imageUrl: string,
  targetWidth = 1080
): Promise<ProcessedImage> {

  const targetHeight = Math.round(targetWidth * 1.25); // 4:5 = 1.25 ratio (portrait)

  console.log('🖼️ V167: Starting crop to 4:5 portrait', {
    url: imageUrl,
    targetWidth,
    targetHeight
  });

  return new Promise((resolve, reject) => {
    const img = new Image();

    // V206.17: Try without CORS first for Google images (they support it)
    // If it fails, we'll catch it in onerror
    if (imageUrl.includes('googleusercontent.com')) {
      img.crossOrigin = 'anonymous';
    }

    img.onerror = (error) => {
      console.error('❌ V206.17: Image load failed', {
        url: imageUrl,
        error: error
      });
      reject(new Error(`Failed to load image: ${imageUrl}`));
    };

    img.onload = async () => {
      console.log('✅ V167: Image loaded', {
        width: img.width,
        height: img.height
      });

      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Calculate center crop dimensions
        const sourceRatio = img.width / img.height;
        const targetRatio = 0.8; // 4:5 portrait (1080/1350 = 0.8)

        let sourceWidth = img.width;
        let sourceHeight = img.height;
        let sourceX = 0;
        let sourceY = 0;

        if (sourceRatio > targetRatio) {
          // Image is wider than target - crop sides
          sourceWidth = img.height * targetRatio;
          sourceX = (img.width - sourceWidth) / 2;
        } else {
          // Image is taller than target - crop top/bottom
          sourceHeight = img.width / targetRatio;
          sourceY = (img.height - sourceHeight) / 2;
        }

        console.log('🖼️ V167: Crop calculations (4:5 portrait)', {
          sourceRatio: sourceRatio.toFixed(2),
          targetRatio,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight
        });

        // Set canvas size to target dimensions
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        // Draw cropped and resized image
        ctx.drawImage(
          img,
          sourceX, sourceY, sourceWidth, sourceHeight, // Source rectangle
          0, 0, targetWidth, targetHeight // Destination rectangle
        );

        console.log('✅ V167: Image drawn to canvas');

        // V167: Convert to WebP with quality optimization to stay under 100KB
        const tryConvertWithQuality = async (quality: number): Promise<Blob | null> => {
          return new Promise((resolveBlob) => {
            canvas.toBlob(
              (blob) => {
                resolveBlob(blob);
              },
              'image/webp',
              quality
            );
          });
        };

        // V167: Try different quality levels to get under 100KB
        let finalBlob: Blob | null = null;
        let quality = 0.85; // Start with high quality

        finalBlob = await tryConvertWithQuality(quality);

        if (finalBlob && finalBlob.size > 100 * 1024) {
          console.log('⚠️ V167: File > 100KB, reducing quality...');
          quality = 0.75;
          finalBlob = await tryConvertWithQuality(quality);
        }

        if (finalBlob && finalBlob.size > 100 * 1024) {
          console.log('⚠️ V167: File still > 100KB, further reduction...');
          quality = 0.65;
          finalBlob = await tryConvertWithQuality(quality);
        }

        if (finalBlob && finalBlob.size > 100 * 1024) {
          console.log('⚠️ V167: File still > 100KB, final reduction...');
          quality = 0.55;
          finalBlob = await tryConvertWithQuality(quality);
        }

        if (!finalBlob) {
          reject(new Error('Could not create WebP blob'));
          return;
        }

        console.log('✅ V167: WebP blob created', {
          size: `${Math.round(finalBlob.size / 1024)}KB`,
          type: finalBlob.type,
          quality: quality,
          under100KB: finalBlob.size <= 100 * 1024
        });

        resolve({
          blob: finalBlob,
          url: URL.createObjectURL(finalBlob),
          width: targetWidth,
          height: targetHeight
        });
      } catch (error) {
        console.error('❌ V206.17: Canvas processing failed', error);
        reject(error);
      }
    };

    img.src = imageUrl;
  });
}

/**
 * V167: Backward compatibility - redirect old 5:4 calls to new 4:5 function
 * @deprecated Use cropImageTo4x5 instead
 */
export async function cropImageTo5x4(
  imageUrl: string,
  targetWidth = 1400
): Promise<ProcessedImage> {
  console.log('⚠️ V167: cropImageTo5x4 is deprecated, redirecting to cropImageTo4x5');
  // Convert to portrait dimensions (1080x1350 instead of 1400x1120)
  return cropImageTo4x5(imageUrl, 1080);
}

/**
 * Upload processed image to Cloudinary
 * Returns public URL or null on failure
 * V206.18: Switch to Cloudinary (more reliable than Supabase Storage)
 * V207: Updated to use signed upload flow via uploadToCloudinary
 */
export async function uploadProcessedImage(
  blob: Blob,
  userId: string,
  supabase?: SupabaseClient
): Promise<string | null> {

  try {
    const fileName = `${userId}-${Date.now()}.jpg`;

    console.log('📤 V207: Uploading to Cloudinary via signed upload', {
      fileName,
      size: `${Math.round(blob.size / 1024)}KB`,
      type: blob.type
    });

    // V207: Use signed upload flow via uploadToCloudinary
    // Convert blob to File for uploadToCloudinary
    const file = new File([blob], fileName, { type: blob.type });

    // Import and use the signed upload function
    const { uploadToCloudinary } = await import('../lib/cloudinary');
    const publicUrl = await uploadToCloudinary(file, userId);

    console.log('✅ V207: Upload successful via signed flow', publicUrl);

    return publicUrl;

  } catch (error) {
    console.error('❌ V207: Upload failed', error);
    return null;
  }
}

/**
 * Complete pipeline: fetch high-res → crop 4:5 → upload
 * V167: Updated to use 4:5 portrait ratio (1080x1350), WebP <100KB
 * This is the main function to call from your onboarding flow
 */
export async function processGoogleProfileImage(
  googleImageUrl: string,
  userId: string,
  supabase: SupabaseClient
): Promise<string | null> {

  try {
    console.log('🖼️ V167: Step 1 - Fetching high-res image');
    console.log('📍 Original URL:', googleImageUrl);

    // Step 1: Get high-res URL (2048x2048)
    const highResUrl = getHighResGoogleImageUrl(googleImageUrl);
    console.log('📍 High-res URL:', highResUrl);
    console.log('✅ V167: Step 1 complete - URL transformed');

    // V167: Step 2 - Crop to 4:5 portrait ratio (1080x1350), WebP <100KB
    console.log('🖼️ V167: Step 2 - Cropping to 4:5 portrait ratio');
    const processed = await cropImageTo5x4(highResUrl, 1080); // Uses backward compat function
    console.log('✅ V167: Step 2 complete - Image cropped', {
      width: processed.width,
      height: processed.height,
      blobSize: `${Math.round(processed.blob.size / 1024)}KB`,
      format: 'WebP'
    });

    // Step 3: Upload to Supabase Storage
    console.log('🖼️ V206.12: Step 3 - Uploading to Supabase');
    const publicUrl = await uploadProcessedImage(processed.blob, userId, supabase);
    console.log('✅ V206.12: Step 3 complete - Upload finished');
    console.log('📍 Public URL:', publicUrl);

    // Clean up blob URL
    URL.revokeObjectURL(processed.url);

    return publicUrl;

  } catch (error) {
    console.error('❌ V206.12: Processing pipeline failed', error);
    console.error('❌ Error details:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined
    });
    return null;
  }
}

/**
 * V206.14: Process Google image during user creation
 * V167: Updated to 4:5 portrait ratio (1080x1350), WebP <100KB
 * Fetches 2048×2048 from Google and crops to 1080×1350 (4:5 portrait ratio)
 * Returns Supabase Storage URL or high-res Google URL as fallback
 *
 * @param googleImageUrl - Original Google image URL (usually 96×96)
 * @param userId - User ID for file naming
 * @param supabase - Supabase client
 * @returns Public URL of processed image or fallback high-res Google URL
 */
export async function processGoogleImageForNewUser(
  googleImageUrl: string,
  userId: string,
  supabase: SupabaseClient
): Promise<string | null> {

  try {
    console.log('🖼️ V167: Processing Google image for new user', {
      originalUrl: googleImageUrl,
      userId
    });

    // Step 1: Get high-res URL (2048×2048 instead of 96×96)
    const highResUrl = getHighResGoogleImageUrl(googleImageUrl);
    console.log('📍 V167: High-res URL obtained (2048×2048)');

    // V167: Step 2 - Crop to 4:5 portrait ratio (1080×1350), WebP <100KB
    console.log('🖼️ V167: Cropping to 4:5 portrait ratio (1080×1350)');
    const processed = await cropImageTo5x4(highResUrl, 1080); // Uses backward compat function
    console.log('✅ V167: Cropped successfully', {
      width: processed.width,
      height: processed.height,
      ratio: (processed.width / processed.height).toFixed(2),
      size: `${Math.round(processed.blob.size / 1024)}KB`,
      format: 'WebP'
    });
    
    // Step 3: Upload to Supabase Storage
    console.log('📤 V206.14: Uploading to Supabase Storage');
    const publicUrl = await uploadProcessedImage(
      processed.blob,
      userId,
      supabase
    );
    
    if (publicUrl) {
      console.log('✅ V206.14: Upload successful', publicUrl);
    } else {
      console.warn('⚠️ V206.14: Upload failed, will use high-res fallback');
    }
    
    // Cleanup blob URL
    URL.revokeObjectURL(processed.url);
    
    return publicUrl;
    
  } catch (error) {
    console.error('❌ V206.14: Processing failed', error);

    // Fallback: return high-res Google URL (2048×2048, still better than 96×96!)
    const fallbackUrl = getHighResGoogleImageUrl(googleImageUrl);
    console.log('⚠️ V206.14: Using high-res fallback (2048×2048)', fallbackUrl);
    return fallbackUrl;
  }
}

/**
 * V206.19: Process Google image to Cloudinary with 4:5 crop
 * V167: Updated to 4:5 portrait ratio (1080x1350), WebP <100KB
 * V207.21: Added userName parameter for Cloudinary display name
 * Downloads 2048×2048 Google image, crops to 1080×1350 (4:5 portrait), uploads to Cloudinary
 *
 * @param googleImageUrl - Google profile image URL
 * @param userId - User ID for Cloudinary public_id
 * @param userName - User's name for Cloudinary display name (optional)
 * @returns Cloudinary secure_url or fallback high-res Google URL
 */
export async function processGoogleImageToCloudinary(
  googleImageUrl: string,
  userId: string,
  userName?: string
): Promise<string | null> {

  try {
    console.log('🖼️ V207.21: Processing Google image to Cloudinary (4:5 portrait WebP <100KB)');
    console.log('📍 V207.21: Input URL:', googleImageUrl);
    console.log('📍 V207.21: User ID:', userId);
    console.log('📍 V207.21: User Name:', userName || 'Not provided (will fetch from DB)');

    // Step 1: Get high-res URL (2048×2048)
    const highResUrl = googleImageUrl.split('=s')[0] + '=s2048';
    console.log('📍 V207.21: High-res URL:', highResUrl);

    // V167: Step 2 - Crop to 4:5 portrait (1080×1350), WebP <100KB
    console.log('🖼️ V207.21: Starting crop to 4:5 portrait...');
    const processed = await cropImageTo5x4(highResUrl, 1080); // Uses backward compat function
    console.log('✅ V207.21: Image cropped to 4:5 portrait', {
      width: processed.width,
      height: processed.height,
      size: `${Math.round(processed.blob.size / 1024)}KB`,
      type: processed.blob.type,
      format: 'WebP'
    });

    // Step 3: Convert to WebP format
    console.log('🖼️ V207.21: Converting to WebP...');
    const webpBlob = await convertToWebP(processed.blob);
    console.log('✅ V207.21: Converted to WebP', {
      size: `${Math.round(webpBlob.size / 1024)}KB`,
      type: webpBlob.type
    });

    // V207.21: Fetch userName from database if not provided
    let finalUserName = userName;
    if (!finalUserName) {
      console.log('📋 V207.21: No userName provided, fetching from database');
      try {
        const { supabase } = await import('../integrations/supabase/client');
        const { data: userData } = await supabase
          .from('users')
          .select('name, google_name')
          .eq('id', userId)
          .single();

        // V334-RA06: Use name only — user.name is single source of truth for display
        finalUserName = userData?.name || undefined;
        console.log('📋 V207.21: Retrieved from DB - name:', userData?.name, 'google_name:', userData?.google_name);
        console.log('📋 V207.21: Using finalUserName:', finalUserName);
      } catch (err) {
        console.error('❌ V207.21: Exception fetching userName:', err);
      }
    } else {
      console.log('📋 V207.21: Using provided userName:', finalUserName);
    }

    // Step 4: Upload to Cloudinary as WebP (using signed upload flow)
    console.log('📤 V207.21: Uploading to Cloudinary via signed upload...');
    console.log('📤 V207.21: Upload details:', {
      publicId: userId,
      displayName: finalUserName,
      format: 'webp',
      size: `${Math.round(webpBlob.size / 1024)}KB`,
      overwrite: true
    });

    // V207.21: Use finalUserName for File object name (Cloudinary display name)
    const fileName = finalUserName ? `${finalUserName}.webp` : `${userId}.webp`;
    const file = new File([webpBlob], fileName, { type: 'image/webp' });
    console.log('📋 V207.21: File object name:', fileName);

    const { uploadToCloudinary } = await import('../lib/cloudinary');
    const secureUrl = await uploadToCloudinary(file, userId, finalUserName);

    console.log('✅ V207.21: Cloudinary upload successful via signed flow!');
    console.log('📸 V207.21: Image URL (WebP):', secureUrl);

    // Clean up blob URLs
    URL.revokeObjectURL(processed.url);

    return secureUrl;

  } catch (error) {
    console.error('❌ V206.22: Processing failed with error:', error);
    console.error('❌ V206.22: Error details:', {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      name: error instanceof Error ? error.name : undefined
    });

    // Fallback: return high-res Google URL (still better than 96×96)
    const fallbackUrl = googleImageUrl.split('=s')[0] + '=s2048';
    console.log('⚠️ V206.22: Using high-res Google fallback:', fallbackUrl);
    return fallbackUrl;
  }
}

/**
 * V206.21: Convert blob to WebP format
 */
async function convertToWebP(blob: Blob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0);

      canvas.toBlob(
        (webpBlob) => {
          URL.revokeObjectURL(url);
          if (webpBlob) {
            resolve(webpBlob);
          } else {
            reject(new Error('Failed to convert to WebP'));
          }
        },
        'image/webp',
        0.92 // 92% quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}
